/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable, signal } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class PwaInstallService {
  private deferredPrompt: any = null;
  private showInstallPrompt = signal<boolean>(false);
  private showEdgeBrowserBanner = signal<boolean>(false);
  private showIosBrowserBanner = signal<boolean>(false);

  private isIos = this.checkIsIos();
  private isStandalone = this.checkIsStandalone();
  private isEdgeAndroid = this.checkIsEdgeAndroid();
  private isSafariIos = this.checkIsSafariIos();

  constructor() {
    this.initInstallPrompt();
  }

  get canInstall() {
    return this.showInstallPrompt.asReadonly();
  }

  get showEdgeBrowserMessage() {
    return this.showEdgeBrowserBanner.asReadonly();
  }

  get showIosBrowserMessage() {
    return this.showIosBrowserBanner.asReadonly();
  }

  private checkIsEdgeAndroid(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    return /edga/.test(ua) && /android/.test(ua);
  }

  private checkIsIos(): boolean {
    const ua = navigator.userAgent;
    const isAppleDevice = /iPad|iPhone|iPod/.test(ua);
    const isIpadOs =
      navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

    const screenMin = Math.min(window.screen.width, window.screen.height);
    const screenMax = Math.max(window.screen.width, window.screen.height);
    const isIpadLikeScreen =
      screenMin >= 768 && screenMax >= 1024 && screenMax <= 1366;
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    const isIpadOsDesktopUa =
      navigator.platform === 'MacIntel' && hasTouch && isIpadLikeScreen;

    return isAppleDevice || isIpadOs || isIpadOsDesktopUa;
  }

  private checkIsSafariIos(): boolean {
    const ua = navigator.userAgent;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
    return this.checkIsIos() && isSafari;
  }

  private checkIsStandalone(): boolean {
    // Check if app is already installed as PWA
    return (window.navigator as any).standalone === true;
  }

  private initInstallPrompt(): void {
    // For Edge Android - show message to use Chrome instead
    if (this.isEdgeAndroid && !this.isStandalone) {
      console.log(
        '[PWA DEBUG] Edge Android detected - showing Chrome recommendation banner',
      );
      this.showEdgeBrowserBanner.set(true);
      return;
    }

    // For iOS, show Safari guidance or install instructions
    if (this.isIos && !this.isStandalone) {
      if (!this.isSafariIos && this.shouldShowIosBrowserPrompt()) {
        console.log(
          '[PWA DEBUG] iOS detected but not Safari, showing Safari guidance banner',
        );
        this.showIosBrowserBanner.set(true);
        return;
      }

      if (this.isSafariIos && this.shouldShowPrompt()) {
        console.log(
          '[PWA DEBUG] iOS Safari detected and not standalone, showing install banner',
        );
        this.showInstallPrompt.set(true);
        return;
      }
    }

    // For Android and other browsers
    try {
      window.addEventListener('beforeinstallprompt', (e: any) => {
        try {
          // Prevent the mini-infobar from appearing on mobile
          e.preventDefault();
          // Stash the event so it can be triggered later
          this.deferredPrompt = e;
          console.log('beforeinstallprompt captured');
          // Show install banner
          this.showInstallPrompt.set(true);
        } catch (error) {
          console.error('Error handling beforeinstallprompt:', error);
        }
      });

      // Check if app is already installed
      window.addEventListener('appinstalled', () => {
        console.log('App installed');
        this.showInstallPrompt.set(false);
        this.deferredPrompt = null;
        this.isStandalone = this.checkIsStandalone();
      });
    } catch (error) {
      console.error('Error setting up install listeners:', error);
    }
  }

  async installPwa(): Promise<void> {
    if (!this.deferredPrompt) {
      console.warn('Install prompt not available. Attempting fallback...');
      // Fallback: Open Play Store or guide user
      this.openPlayStore();
      return;
    }

    try {
      // Show the install prompt
      this.deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await this.deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }

      // Hide the banner
      this.showInstallPrompt.set(false);
      this.deferredPrompt = null;
    } catch (error) {
      console.error('Error showing install prompt:', error);
      this.openPlayStore();
    }
  }

  private openPlayStore(): void {
    const userAgent = navigator.userAgent.toLowerCase();

    if (userAgent.includes('android')) {
      // Open Android Chrome menu to install
      alert(
        'Tap the menu (⋮) and select "Install app" or "Add to Home Screen"',
      );
    } else {
      console.log('Please install via your app store or browser menu');
    }
  }

  dismissPrompt(): void {
    this.showInstallPrompt.set(false);
    // Store dismissal in localStorage to not show again for some time
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  }

  dismissEdgeBannerPrompt(): void {
    this.showEdgeBrowserBanner.set(false);
    localStorage.setItem('edge-banner-dismissed', Date.now().toString());
    console.log('[PWA DEBUG] Edge browser banner dismissed');
  }

  dismissIosBrowserBanner(): void {
    this.showIosBrowserBanner.set(false);
    localStorage.setItem('ios-safari-banner-dismissed', Date.now().toString());
    console.log('[PWA DEBUG] iOS Safari banner dismissed');
  }

  openInChrome(): void {
    const currentUrl = window.location.href;
    const trustedHost = this.getTrustedHost();

    if (!trustedHost) {
      console.warn('[PWA DEBUG] Invalid host, skipping Chrome intent redirect');
      alert('Please open this URL in Google Chrome browser:\n' + currentUrl);
      return;
    }

    // Intent URL scheme for Chrome (Android)
    const chromePackage = 'com.android.chrome';
    const intentUrl = `intent://${trustedHost}/#Intent;scheme=https;package=${chromePackage};end`;

    try {
      window.location.href = intentUrl;
      console.log('[PWA DEBUG] Attempting to open in Chrome');
    } catch (e) {
      console.error('[PWA DEBUG] Error opening Chrome:', e);
      // Fallback: show message
      alert('Please open this URL in Google Chrome browser:\n' + currentUrl);
    }
  }

  private getTrustedHost(): string | null {
    try {
      const host = window.location.host;
      // Allow only host characters valid for domain/ip + optional port.
      if (!/^[a-zA-Z0-9.-]+(?::\d+)?$/.test(host)) {
        return null;
      }
      return host;
    } catch {
      return null;
    }
  }

  openInSafari(): void {
    const currentUrl = window.location.href;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard
        .writeText(currentUrl)
        .then(() => {
          alert(
            'Link copied. Please open Safari and paste the link to install the app.',
          );
        })
        .catch(() => {
          alert('Please open Safari and paste this link:\n' + currentUrl);
        });
      return;
    }

    alert('Please open Safari and paste this link:\n' + currentUrl);
  }

  shouldShowIosBrowserPrompt(): boolean {
    const dismissed = localStorage.getItem('ios-safari-banner-dismissed');
    if (!dismissed) return true;

    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - parseInt(dismissed) > sevenDays;
  }

  shouldShowPrompt(): boolean {
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (!dismissed) return true;

    // Show again after 7 days
    const sevenDays = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - parseInt(dismissed) > sevenDays;
  }

  isIOS(): boolean {
    return this.isIos;
  }

  isAlreadyInstalled(): boolean {
    return this.checkIsStandalone();
  }
}
